import React, { Fragment, useState } from 'react';
import { useField } from 'react-final-form';

import {
  DndContext,
  type DragMoveEvent,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box, Stack, Typography } from '@mui/material';
import { useSetCurrentBlockId } from '@wp-block-builder/editor/hooks';
import { type TBlock } from '@wp-block-builder/package';

import BlockView from './BlockView';
import GhostBlockView from './GhostBlockView';
import applyDepth from './helpers/applyDepth';
import rebuildDocumentTree from './helpers/blocksFromFlatDocument';
import { type TFlatBlock } from './helpers/blocksToFlatDocument';
import { getDeltaOffsetDepth } from './helpers/paddingCalculators';
import pluckApplyDepth from './helpers/pluckApplyDepth';
import InsertionCursor from './InsertionCursor';
import useBlockState, { type TBlockStatus } from './useBlockState';
import useFlatDocumentTree from './useFlatDocumentTree';
import useProjectedDepth from './useProjectedDepth';

function getBlockStatus(flatTree: TFlatBlock[], id: string): TBlockStatus {
  const index = flatTree.findIndex((t) => t.id === id);
  return { id, index, flatBlock: flatTree[index] };
}

const activationConstraint = { delay: 100, tolerance: 250 };
type TOverBlockStatus = {
  id: string;
  position: 'before' | 'after';
};

export default function NavigatorSidebarContents() {
  const { onChange } = useField<TBlock[]>('configuration.blocks').input;
  const flatDocumentTree = useFlatDocumentTree();
  const [active, setActive] = useBlockState();
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [overBlockStatus, setOverBlockStatus] = useState<TOverBlockStatus | null>(null);
  const depth = useProjectedDepth({ items: flatDocumentTree, active, overBlockStatus, offset: offsetLeft });
  const mouseSensor = useSensor(MouseSensor, { activationConstraint });
  const touchSensor = useSensor(TouchSensor, { activationConstraint });
  const sensors = useSensors(mouseSensor, touchSensor);
  const setCurrentBlockId = useSetCurrentBlockId();

  return (
    <Stack width="100%">
      <Box p={3}>
        <Typography variant="overline" color="text.secondary">
          Navigator
        </Typography>
        <Typography variant="body2">Drag and drop to move blocks within this panel.</Typography>
      </Box>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {flatDocumentTree.map((fBlock) => (
          <Fragment key={fBlock.id}>
            <InsertionCursor blockId={fBlock.id} position="before" overBlockStatus={overBlockStatus} depth={depth} />
            <BlockView setOverBlockStatus={setOverBlockStatus} activeBlock={active?.flatBlock} flatBlock={fBlock} />
            <InsertionCursor blockId={fBlock.id} position="after" overBlockStatus={overBlockStatus} depth={depth} />
          </Fragment>
        ))}
        <GhostBlockView flatBlock={active?.flatBlock} />
      </DndContext>
    </Stack>
  );

  function handleDragStart({ active }: DragStartEvent) {
    const activeId = active.id as string;
    setCurrentBlockId(activeId);
    setActive(getBlockStatus(flatDocumentTree, activeId));
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(getDeltaOffsetDepth(delta.x));
  }

  function handleDragEnd() {
    resetState();
    if (active === null || overBlockStatus === null) {
      return;
    }

    let result: TFlatBlock[] = [];
    if (active.id === overBlockStatus.id) {
      result = applyDepth(flatDocumentTree, active.id, depth);
    } else {
      const blockResult = pluckApplyDepth(flatDocumentTree, active.id, depth);
      const blockResultIds = blockResult.map((k) => k.id);
      result = flatDocumentTree.filter((b) => !blockResultIds.includes(b.id));
      const startIndex = result.findIndex((b) => b.id === overBlockStatus.id);
      if (overBlockStatus.position === 'before') {
        result.splice(startIndex, 0, ...blockResult);
      } else {
        result.splice(startIndex + 1, 0, ...blockResult);
      }
    }
    onChange(rebuildDocumentTree(result));
  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setActive(null);
    setOverBlockStatus(null);
    setOffsetLeft(0);
  }
}
